import { useTranslation } from "react-i18next";
import { forwardRef, useEffect, useRef } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { ChevronRight, Star, Check, X, Loader2, ArchiveRestore, CornerDownRight } from "lucide-react";
import { TextField } from "react-aria-components";
import type { FacilityNode } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { facilityNodeIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import type { FlatRow } from "./facility-tree.js";
import { canDrop } from "./facility-tree.js";
import { FacilityCountsMeta } from "./FacilityCountsMeta.js";
import { FacilityKebabMenu } from "./FacilityKebabMenu.js";

/**
 * One row of the facility timeline tree — presentational (`role="treeitem"`). The parent `FacilityTree` owns expand +
 * selection + focus (roving tabindex) and keyboard nav; this only renders + emits intent.
 *
 * Layout: a left RAIL carries a continuous connector line with the node's dot (● branch / ○ leaf) — stacked rows form
 * one line root→leaf. To its right sits the facility CARD: a role glyph ({@link facilityNodeIcon}: root/branch/leaf) ·
 * name · count · star · kebab (⋮). Clicking the card SELECTS the facility; the chevron toggles expand; the actions
 * don't bubble to select. All non-favorite actions live in the kebab (rename inline, add child, move, manage, and the
 * disabled Phase-2 stubs).
 *
 * Move (P6.2): a **movable** card is a dnd-kit draggable (grab it onto another card to reparent) AND a droppable drop
 * target (highlighted only for a valid drop — `canDrop`). Grabbing the chevron/star/kebab controls never starts a drag
 * (pointerdown stopped). The keyboard/SR path is the kebab's "Move to…" action (opens the move dialog) — dnd is
 * pointer-only. While the row is being renamed inline, drag + select are suppressed and the field stops key
 * propagation so the tree's arrow/space/'f' nav can't hijack the caret.
 */
/**
 * Per-row state for the MOVE PICKER (present ⇒ the row renders in "picker" mode inside {@link MoveFacilityDrawer}):
 * no star/kebab/"+"/drag, the moving subtree greyed + badged, invalid targets disabled, and a valid target shows an
 * inline "↳ Move here" (the ONLY commit — a whole-row click never moves; ADR-006 panel round-2 #1).
 */
export interface PickerRowState {
  /** A valid destination parent (`canDrop`) — shows "↳ Move here" + accepts Enter to pick. */
  pickable: boolean;
  /** This is the facility being moved — greyed + a MOVING badge. */
  isSource: boolean;
  /** Inside the moving subtree (a descendant of the source) — greyed, never a valid target. */
  inSubtree: boolean;
  /** Commit the move to THIS node as the new parent. */
  onPickHere: () => void;
}

export interface FacilityNodeRowProps {
  row: FlatRow;
  index: number;
  /** When set, the row renders in move-PICKER mode (see {@link PickerRowState}); absent ⇒ normal "browse" row. */
  picker?: PickerRowState | null;
  /** Entrance delay (ms) when this row was just revealed by an expand — staggered by DFS order for the top-to-bottom
   *  "domino" cascade. `null` ⇒ no entrance animation (initial render, a row that was already visible, reduced-motion). */
  revealDelay: number | null;
  /** Fired when THIS row gains focus while still tagged (mid-cascade) — the parent ends the cascade so focus never
   *  rests on a row held invisible by the entrance animation (a11y). */
  onRevealedRowFocus: () => void;
  isMatch: boolean;
  isFavorite: boolean;
  selected: boolean;
  tabbable: boolean;
  /** The caller can move this node (best-effort mirror of the server; drives the drag handle + kebab "Move to…"). */
  movable: boolean;
  /** The caller is the bypass tier — affects which drop targets are valid highlights. */
  bypass: boolean;
  /** Node-level manage (manage/access_manage or bypass) — gates the kebab's Rename / Add child / Manage facility. */
  canManage: boolean;
  /** Subtree-wide manage (mirror of the server's requireManageSubtree) — gates Archive (kebab) + Unarchive (archived view). */
  canManageSubtree: boolean;
  /** The caller can Duplicate this node (subtree manage AND create-authz on the parent; root = bypass-only) — gates Duplicate. */
  canDuplicate: boolean;
  /** The caller can Insert above this node (create a SIBLING = a child of the node's parent; ADR-009) — gates Insert above. */
  canInsertAbove: boolean;
  /** This archived row is the TOP of its archived subtree — only these show Unarchive (a descendant of an archived
   *  node can't be restored while its ancestor is archived). */
  isArchiveRoot: boolean;
  /** The Archived filter is active — rows are non-selectable; the only action is Unarchive (on archived subtree roots). */
  archivedMode: boolean;
  /** This row is in inline-rename mode — swap the name for an edit field + save/cancel. */
  editing: boolean;
  /** The draft name while editing (controlled by the parent so it survives re-renders). */
  editName: string;
  /** The rename mutation is in flight — disable Save + show a spinner. */
  savePending: boolean;
  onEditNameChange: (v: string) => void;
  onToggleExpand: (id: number) => void;
  onToggleFavorite: (id: number) => void;
  onSelect: (id: number) => void;
  onFocusRow: (index: number) => void;
  onMoveTo: (node: FacilityNode) => void;
  onAddChild: (node: FacilityNode) => void;
  onManage: (node: FacilityNode) => void;
  onArchive: (node: FacilityNode) => void;
  onDuplicate: (node: FacilityNode) => void;
  onInsertAbove: (node: FacilityNode) => void;
  onDelete: (node: FacilityNode) => void;
  onUnarchive: (node: FacilityNode) => void;
  onStartRename: (node: FacilityNode) => void;
  onSaveRename: () => void;
  onCancelRename: () => void;
}

export const FacilityNodeRow = forwardRef<HTMLDivElement, FacilityNodeRowProps>(
  function FacilityNodeRow(
    {
      row,
      index,
      picker,
      revealDelay,
      onRevealedRowFocus,
      isMatch,
      isFavorite,
      selected,
      tabbable,
      movable,
      bypass,
      canManage,
      canManageSubtree,
      canDuplicate,
      canInsertAbove,
      isArchiveRoot,
      archivedMode,
      editing,
      editName,
      savePending,
      onEditNameChange,
      onToggleExpand,
      onToggleFavorite,
      onSelect,
      onFocusRow,
      onMoveTo,
      onAddChild,
      onManage,
      onArchive,
      onDuplicate,
      onInsertAbove,
      onDelete,
      onUnarchive,
      onStartRename,
      onSaveRename,
      onCancelRename,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const { node, depth, expanded, hasChildren } = row;
    const openable = node.permission !== null;
    const NodeIcon = facilityNodeIcon(node);
    const isPicker = picker != null; // move-picker mode (inside MoveFacilityDrawer) — no star/kebab/"+"/drag/select
    const pickerDimmed = isPicker && (picker.isSource || picker.inSubtree || !picker.pickable);

    // dnd-kit: the card is a draggable (movable rows only) AND a droppable target. Drag state comes from the context via
    // the droppable's `active`; we render a DragOverlay in the parent, so the origin only dims (no transform applied here).
    // Both are OFF in picker mode — the drawer's "↳ Move here" is the only commit, not drag/drop.
    const drag = useDraggable({
      id: node.id,
      data: { node },
      disabled: !movable || editing || archivedMode || isPicker,
    });
    const drop = useDroppable({ id: node.id, data: { node }, disabled: isPicker });
    const activeNode = drop.active?.data.current?.node as
      | FacilityNode
      | undefined;
    const dragging = drag.isDragging;
    const isValidTarget =
      !!activeNode &&
      activeNode.id !== node.id &&
      canDrop(activeNode, node, bypass);
    const showDropHere = drop.isOver && isValidTarget;
    const dimForDrag = !!activeNode && !dragging && !isValidTarget; // a non-target while something else is dragging

    const setCardRef = (el: HTMLDivElement | null) => {
      if (isPicker) return; // no dnd wiring in the picker
      drop.setNodeRef(el);
      if (movable && !archivedMode) drag.setNodeRef(el);
    };
    // Archived rows are non-selectable (no manuals / no right pane) — the Archived tab is restore-only (panel #1/#3).
    // Picker rows are non-selectable too — a row click is navigation-only; only "↳ Move here" commits (panel round-2 #1).
    const selectable = openable && !editing && !archivedMode && !isPicker;
    // Stop a pointerdown on the interactive controls from starting a drag on the card.
    const noDrag = {
      onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
    };

    // Compose the forwarded ref (FacilityTree's roving-tabindex focus list) with an internal ref so we can restore
    // focus to THIS row when inline rename ends — the focused edit input unmounts, and without this focus falls to
    // <body>, dropping keyboard/SR users out of the tree (a11y acceptance criterion; panel finding).
    const innerRef = useRef<HTMLDivElement | null>(null);
    const wasEditing = useRef(false);
    const setRootRef = (el: HTMLDivElement | null) => {
      innerRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    };
    useEffect(() => {
      if (wasEditing.current && !editing) innerRef.current?.focus();
      wasEditing.current = editing;
    }, [editing]);

    return (
      <div
        ref={setRootRef}
        role="treeitem"
        aria-level={depth + 1}
        aria-expanded={hasChildren ? expanded : undefined}
        aria-selected={selected}
        tabIndex={tabbable ? 0 : -1}
        data-facility-id={node.id}
        // Track roving focus; and if focus lands here while this row is still animating in (mid-cascade), end the whole
        // cascade so focus is never held on an invisible row (the entrance keeps it at opacity 0 until its delay — a11y).
        onFocus={() => {
          onFocusRow(index);
          if (revealDelay !== null) onRevealedRowFocus();
        }}
        // Ancestor placeholders (no permission) are navigable but NOT selectable — selecting one must not drive the
        // (soon permission-scoped) detail pane. Gate the click; the tree gates Enter too (panel finding). Also don't
        // select while renaming (the edit field handles its own clicks) or in the Archived tab (restore-only).
        onClick={selectable ? () => onSelect(node.id) : undefined}
        // No vertical padding: rows touch so the rail lines form ONE continuous timeline; the card's `my` gives the
        // visual gap between cards while the line runs unbroken behind them. Indent grows the rail per depth.
        className={cn(
          "group flex items-stretch gap-1 outline-none",
          selectable ? "cursor-pointer" : "cursor-default",
          // Expand cascade: a just-revealed row fades + slides in from just above, delayed by its DFS order (set on the
          // style below) so the subtree reveals top-to-bottom. `fill-mode-both` holds it hidden until its turn; only NEW
          // rows mount on expand so existing rows never re-animate. `motion-reduce` is the CSS backstop (the parent also
          // skips tagging under reduced-motion). Duration is the reduced-motion-aware token.
          revealDelay !== null &&
            "motion-reduce:animate-none animate-in fade-in-0 slide-in-from-top-3 fill-mode-both",
        )}
        style={{
          paddingLeft: `calc(${depth} * 1.5rem)`,
          ...(revealDelay !== null
            ? { animationDelay: `${revealDelay}ms`, animationDuration: "var(--duration-base)" }
            : null),
        }}
      >
        {/* Timeline rail: full-height connector line + the node's dot centered on it. */}
        <div
          aria-hidden="true"
          className="relative flex w-5 shrink-0 items-center justify-center self-stretch"
        >
          <span className="bg-border absolute inset-y-0 left-1/2 w-px -translate-x-1/2" />
          <span
            className={cn(
              "bg-background relative z-10 size-2.5 rounded-full border-2",
              hasChildren ? "border-primary" : "border-border",
              hasChildren && expanded && "bg-primary",
            )}
          />
        </div>

        {/* Facility card — also the dnd drag handle + drop target (browse only). */}
        <div
          ref={setCardRef}
          {...(movable && !editing && !isPicker ? drag.listeners : {})}
          className={cn(
            "relative my-1 flex min-h-11 flex-1 items-center gap-2 rounded-lg border px-3 py-1.5 transition-all",
            selected
              ? "border-primary bg-background shadow-sm ring-1 ring-primary"
              : "border-border bg-background hover:shadow-sm",
            "group-focus-visible:ring-ring/50 group-focus-visible:ring-3",
            !openable && "opacity-70",
            movable && !editing && !archivedMode && !isPicker && "cursor-grab active:cursor-grabbing",
            dragging && "opacity-40",
            dimForDrag && "opacity-60",
            showDropHere && "border-primary bg-primary/5 ring-2 ring-primary",
            // Picker: grey the source + its subtree + invalid targets; a dashed ring marks the source being moved.
            pickerDimmed && "opacity-50",
            isPicker && picker.isSource && "border-dashed ring-1 ring-border",
          )}
        >
          {hasChildren ? (
            <span
              role="presentation"
              aria-hidden="true"
              {...noDrag}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(node.id);
              }}
              className="text-muted-foreground hover:text-foreground -ml-1 flex size-5 shrink-0 cursor-pointer items-center justify-center rounded"
            >
              <ChevronRight
                className={cn(
                  "size-4 shrink-0 transition-transform",
                  expanded && "rotate-90",
                )}
              />
            </span>
          ) : null}

          {/* Role glyph (root=Home / branch=Network / leaf=Building2) — a scannable facility mark; tree-local. */}
          <NodeIcon aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />

          {editing ? (
            // Inline rename. stopPropagation on keydown so the tree's arrow/Home/End/Space/'f' handler can't hijack the
            // caret (panel finding #4); click stopped so the card doesn't select behind the field.
            <div
              className="flex flex-1 items-center gap-1"
              {...noDrag}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <TextField
                aria-label={t("facilityManagement.actions.renameNode", { name: node.name })}
                value={editName}
                onChange={onEditNameChange}
                maxLength={255}
                autoFocus
                className="flex-1"
              >
                <Input
                  className="h-8"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (!savePending) onSaveRename(); // guard the keyboard path against a double-submit (panel)
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      onCancelRename();
                    }
                  }}
                />
              </TextField>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("facilityManagement.actions.saveName")}
                isDisabled={savePending}
                onPress={onSaveRename}
              >
                {savePending ? (
                  <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                ) : (
                  <Check className="size-3.5" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("facilityManagement.actions.cancelRename")}
                isDisabled={savePending}
                onPress={onCancelRename}
              >
                <X className="size-3.5" />
              </Button>
            </div>
          ) : (
            <>
              {/* Name (line 1) + a meta line (line 2): small icon + count per segment (subfacilities · categories ·
                  manuals). Each segment is OMITTED when zero — a leaf shows no meta line at all (see FacilityCountsMeta). */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "truncate text-sm",
                      isMatch || selected ? "font-semibold" : "font-medium",
                    )}
                  >
                    {node.name}
                  </span>
                  {node.archived ? (
                    <span className="text-muted-foreground bg-muted shrink-0 rounded px-1.5 py-0.5 text-[0.65rem] uppercase">
                      archived
                    </span>
                  ) : null}
                </div>
                <FacilityCountsMeta
                  className="mt-0.5"
                  subfacilities={node.childCount}
                  categories={node.categoryCount ?? 0}
                  manuals={node.manualCount ?? 0}
                />
              </div>

              {/* Picker trailing: the moving source shows a MOVING badge; a valid target shows an inline "↳ Move here"
                  (the ONLY commit — revealed on hover/focus, always on the active row). Invalid/subtree rows show
                  nothing (just dimmed). No star/kebab/drop-hint in the picker. */}
              {isPicker &&
                (picker.isSource ? (
                  <span className="text-muted-foreground bg-muted shrink-0 rounded px-1.5 py-0.5 text-[0.65rem] font-medium tracking-wide uppercase">
                    {t("facilityManagement.move.moving")}
                  </span>
                ) : picker.pickable ? (
                  <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="sm"
                      excludeFromTabOrder={!tabbable}
                      aria-label={t("facilityManagement.move.moveInto", { name: node.name })}
                      className={cn(
                        "gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100",
                        tabbable && "opacity-100",
                      )}
                      onPress={picker.onPickHere}
                    >
                      <CornerDownRight aria-hidden="true" className="size-3.5" />
                      {t("facilityManagement.move.moveHere")}
                    </Button>
                  </div>
                ) : null)}

              {!isPicker && (
                <>
                  {/* When this row is a valid drop target, a subtle "{t("facilityManagement.move.dropHere")}" hint. */}
                  {showDropHere ? (
                    <span className="text-primary shrink-0 text-xs font-medium">{t("facilityManagement.move.dropHere")}</span>
                  ) : null}

                  {archivedMode ? (
                // Archived tab: the ONLY action, and ONLY on the TOP of an archived subtree. Active ancestor/context
                // rows and archived DESCENDANTS get no action — a descendant can't be restored while its ancestor is
                // archived (server 409s), and Unarchive on an active row would bulk-restore its archived descendants.
                node.archived && isArchiveRoot ? (
                  <div
                    className="shrink-0"
                    {...noDrag}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <TooltipTrigger delay={300}>
                      <Button
                        variant="ghost"
                        size="icon"
                        excludeFromTabOrder={!tabbable}
                        aria-label={t("facilitySelection.nav.unarchiveNode", { name: node.name })}
                        isDisabled={!canManageSubtree}
                        onPress={() => onUnarchive(node)}
                      >
                        <ArchiveRestore className="text-muted-foreground size-4" />
                      </Button>
                      <Tooltip>{t("facilityManagement.actions.unarchive")}</Tooltip>
                    </TooltipTrigger>
                  </div>
                ) : null
              ) : (
                <>
                  {/* Favorite star FIRST, then the kebab (⋮) — the star is the quick toggle; the kebab is the overflow,
                      kept as the rightmost control. stopPropagation (click + keydown + pointerdown) so the star neither
                      selects the row nor starts a drag. */}
                  <div
                    className="shrink-0"
                    {...noDrag}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      excludeFromTabOrder={!tabbable}
                      aria-label={
                        isFavorite ? t("facilitySelection.nav.unfavourite", { name: node.name }) : t("facilitySelection.nav.favourite", { name: node.name })
                      }
                      aria-pressed={isFavorite}
                      onPress={() => onToggleFavorite(node.id)}
                    >
                      <Star
                        className={cn(
                          "size-4",
                          isFavorite
                            ? "fill-attention text-attention"
                            : "text-muted-foreground",
                        )}
                      />
                    </Button>
                  </div>

                  {/* Actions menu (⋮) — the rightmost control; all row actions except the quick favorite. Its popover
                      opens to the RIGHT (into the manuals pane) so it never overlays the tree. stopPropagation (click +
                      keydown + pointerdown) so opening it neither selects the row nor starts a drag. */}
                  <div
                    className="shrink-0"
                    {...noDrag}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <FacilityKebabMenu
                      node={node}
                      isFavorite={isFavorite}
                      canManage={canManage}
                      canArchive={canManageSubtree}
                      canDuplicate={canDuplicate}
                      canInsertAbove={canInsertAbove}
                      movable={movable}
                      tabbable={tabbable}
                      onRename={() => onStartRename(node)}
                      onAddChild={() => onAddChild(node)}
                      onManage={() => onManage(node)}
                      onMoveTo={() => onMoveTo(node)}
                      onArchive={() => onArchive(node)}
                      onDuplicate={() => onDuplicate(node)}
                      onInsertAbove={() => onInsertAbove(node)}
                      onDelete={() => onDelete(node)}
                      onToggleFavorite={() => onToggleFavorite(node.id)}
                    />
                  </div>
                </>
              )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  },
);
